import { Grid, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import type { AxiosError } from 'axios';
import useSWR from 'swr';
import RepairsScanned from './workReassignmentRepairsScanned';
import { emptyIRepair } from './workReassignmentInterfaces';
import type IRepair from './workReassignmentInterfaces';
import InvalidBarcodeDialog from './invalidBarcodeModal';
import useEventListener from '../../lib/useEventListener';
import deselectElementPreventDefaultAndBlur from '../../lib/deselectAndBlur';

interface IWorkReassignmentProp {
  initialBarcode: string;
  setShowReassignmentInterface: React.Dispatch<React.SetStateAction<boolean>>;
  showReassignmentInterface: boolean;
}

const WorkReassignmentDropoff: React.FC<IWorkReassignmentProp> = ({
  initialBarcode, setShowReassignmentInterface, showReassignmentInterface,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [scannedRepairs, setScannedRepairs] = React.useState<IRepair[]>([]);
  const [scannedRepairsObj, setScannedRepairsObj] = React.useState<IRepair>(emptyIRepair);
  const [newBarcode, setNewBarcode] = React.useState<string>('');
  const [invalidBarcode, setInvalidBarcode] = React.useState<string>('');
  const [showInvalidModal, setShowInvalidModal] = React.useState<boolean>(false);

  const handleFetchRepair = (barcode: string) => {
    axios.post<IRepair>('/workReassignment/GetRepair', {
      RepairBarcode: barcode,
    })
      .then((result) => {
        if (result.data) {
          if (!scannedRepairs.find((r) => r?.barcode === barcode)) {
            setScannedRepairsObj(result.data);
          } else {
            enqueueSnackbar('Duplicate barcode scanned', { persist: false, variant: 'error' });
          }
        } else {
          setInvalidBarcode(barcode);
          setShowInvalidModal(true);
        }
        return result.data;
      })
      .catch((error: AxiosError) => {
        if (error?.message) {
          enqueueSnackbar(error.message, { persist: false, variant: 'error' });
        }
      });
  };

  // Variables to hold barcode buffers for honeywell and banner scanners.
  let bannerBuffer = '';
  let honeywellBuffer = '';

  // Function to handle keystrokes for Banner scanners
  const handleBannerInput = (e: KeyboardEvent) => {
    if (e.key && (e.key !== 'Shift')) {
      bannerBuffer = `${bannerBuffer}${e.key}`;

      if (bannerBuffer.toLowerCase().includes('in:')) {
        bannerBuffer = '';
      }
      if (bannerBuffer.includes('{*|}')) {
        deselectElementPreventDefaultAndBlur(e);

        if (showReassignmentInterface) {
          handleFetchRepair(bannerBuffer.replace('{*|}', ''));
          bannerBuffer = '';
          honeywellBuffer = '';
        }
      }
    }
  };

  // Function to handle keystrokes for HoneyWell scanners
  const handleHoneywellInput = (e: KeyboardEvent) => {
    if (e.key && (e.key.length === 1 || e.key === 'Enter')) {
      if (e.key !== 'Enter') {
        honeywellBuffer = `${honeywellBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented) {
          if (showReassignmentInterface && honeywellBuffer.length > 0) {
            handleFetchRepair(honeywellBuffer.replace('{*|}', ''));
            honeywellBuffer = '';
          }
        }
        honeywellBuffer = '';
        bannerBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBannerInput);
  useEventListener('keydown', handleHoneywellInput);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data } = useSWR(
    ['/workReassignment/GetRepair'],
    () => handleFetchRepair(initialBarcode), {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={1} />
        <Grid item xs={8} container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            <Alert severity="info">
              {'Continue scanning or manually enter in repairs that need to be reassigned.'}
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="courier-input"
              variant="outlined"
              size="small"
              value={newBarcode}
              onChange={
                (event) => {
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  setNewBarcode((event.target as HTMLInputElement).value);
                }
              }
              onKeyPress={
                (event) => {
                  if (event.key === 'Enter' && event.target) {
                    event.preventDefault();
                    event.stopPropagation();
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    handleFetchRepair((event.target as HTMLInputElement).value);
                    setNewBarcode('');
                  }
                }
              }
              label="Barcode"
            />
          </Grid>
          <Grid item xs={12}>
            {
              scannedRepairs
              && (
              <RepairsScanned
                currentScannedRepair={scannedRepairsObj}
                scannedRepairs={scannedRepairs}
                setScannedRepairs={setScannedRepairs}
                showLandingPage={setShowReassignmentInterface}
              />
              )
            }
          </Grid>
        </Grid>
        <InvalidBarcodeDialog
          showInvalidModal={showInvalidModal}
          onClose={() => {
            setShowInvalidModal(false);
          }}
          barcode={invalidBarcode}
        />
      </Grid>
    </>
  );
};

export default WorkReassignmentDropoff;
